import { Close, SearchOutlined } from "@mui/icons-material";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Container } from "@mui/system";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import {
  AccountAPI,
  ContactAPI,
  OrganizationAPI,
  AddressAPI,
} from "../../../../../api";
import SearchDirectory from "../../../../../components/core/GlobalSearch/SearchDirectory";
import { SubmitButton } from "../../../../../components/elements";
import { useAlert } from "../../../../../lib/alert";
import { Account } from "../../../../../model";
import {
  AccountSearchOption,
  ESAccount,
  ESMember,
  ESOrganization,
} from "../../../../../model/AccountSearchOption";
import {
  Address,
  OrganizationAddressLabels,
} from "../../../../../model/Address";
import { ContactLabels, ContactType } from "../../../../../model/Contact";
import { AccountCreateSimpleForm } from "../../../../accounts/components/AccountCreateSimpleForm";
import useAccountExecution from "../../../../accounts/hooks/useAccountExecution";
import { getAccountName } from "../../../../accounts/utils";
import {
  TaskCreateAccount,
  TaskCreateAccountExecution,
  TaskExecutionComponentProps,
} from "../../../types";

type cardData = {
  name: string | undefined;
  secondaryName: string | undefined;
  hasEmail: boolean;
  email: string | undefined;
  logo: string | undefined;
  address: Address | undefined;
};

const OptionSelectedCard = ({
  setSelected,
  disabled,
  loadedData,
}: {
  setSelected: React.Dispatch<
    React.SetStateAction<AccountSearchOption | undefined>
  >;
  disabled?: boolean;
  loadedData?: cardData;
}) => {
  if (!loadedData) return;
  const { name, secondaryName, hasEmail, email, logo, address } = loadedData;

  return (
    <>
      <Container disableGutters sx={{ mb: "1rem" }}>
        <Paper
          elevation={0}
          sx={{
            minWidth: "600px",
            height: "7rem",
            p: 2,
            backgroundColor: "rgba(0, 0, 0, 0.03)",
            borderRadius: "7px",
            boxShadow: "0 2px 1px rgba(0,0,0,0.1)",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              width: "100%",
              height: "100%",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                width: "100%",
                height: "100%",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexGrow: 1,
                  overflow: "hidden",
                  wordBreak: "break-word",
                  flexShrink: 1,
                }}
              >
                {logo && (
                  <img
                    src={logo}
                    style={{ width: "5rem", marginRight: "1rem" }}
                  />
                )}
                <Box
                  minWidth={110}
                  sx={{ display: "flex", flexDirection: "column" }}
                >
                  <Tooltip
                    title={name ?? ""}
                    disableHoverListener={!name || name.length < 80}
                    placement="top"
                    arrow
                  >
                    <Typography
                      variant="inherit"
                      sx={{
                        fontWeight: 500,
                        lineHeight: 1.2,
                        maxHeight: "3.6rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {name}
                    </Typography>
                  </Tooltip>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "grey",
                      mt: 0.5,
                      lineHeight: 1.2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {secondaryName}
                  </Typography>
                  {email && (
                    <Tooltip
                      title={email}
                      disableHoverListener={!email || email.length < 35}
                      placement="bottom"
                      arrow
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: (theme) => theme.palette.grey[600],
                          mt: 0.5,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        <Link
                          to={`mailto:${email}`}
                          style={{ color: "inherit" }}
                        >
                          {email}
                        </Link>
                      </Typography>
                    </Tooltip>
                  )}
                </Box>
              </Box>
              {address && (
                <Tooltip
                  title={[
                    address.first_line,
                    address.second_line,
                    `${address.city}, ${address.state} ${address.zip_code}`,
                  ]
                    .filter(Boolean)
                    .join("\n")}
                  disableHoverListener={!address}
                  placement="top"
                  arrow
                >
                  <Box
                    minWidth={110}
                    maxWidth={"40%"}
                    sx={{
                      ml: 3,
                      overflow: "auto",
                      wordBreak: "break-all",
                      flexShrink: 1,
                      justifyContent: "right",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: (theme) => theme.palette.grey[600],
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {address?.first_line}
                    </Typography>
                    {address?.second_line && (
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: (theme) => theme.palette.grey[600],
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          display: "-webkit-box",
                        }}
                      >
                        {address?.second_line}
                      </Typography>
                    )}
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: (theme) => theme.palette.grey[600],
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        display: "-webkit-box",
                      }}
                    >
                      {`${address?.city}, ${address?.state} ${address?.zip_code}`}
                    </Typography>
                  </Box>
                </Tooltip>
              )}
              <IconButton
                onClick={() => setSelected(undefined)}
                size="small"
                disabled={disabled}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  alignSelf: "center",
                }}
              >
                <Close />
              </IconButton>
            </Box>
          </Box>
        </Paper>
      </Container>
      {!hasEmail && (
        <Alert severity="warning" sx={{ mb: "1rem" }}>
          There is no email associated with this account, and they will not be
          notified.
        </Alert>
      )}
    </>
  );
};

const SearchBar = ({
  query,
  setQuery,
  disabled,
  loading,
}: {
  query: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  disabled?: boolean;
  loading?: boolean;
}) => {
  return (
    <TextField
      placeholder="Search Directory"
      fullWidth
      value={query}
      onChange={(e) => setQuery(e.target.value)}
      disabled={disabled}
      sx={{ mb: "1rem", minWidth: "600px" }}
      InputProps={{
        startAdornment: <SearchOutlined sx={{ mr: 1 }} />,

        endAdornment:
          query && loading ? (
            <IconButton>
              <CircularProgress
                size={20}
                sx={{
                  color: (theme) => theme.palette.grey[600],
                }}
              />
            </IconButton>
          ) : query ? (
            <Tooltip title="Clear Search">
              <IconButton onClick={() => setQuery("")} disabled={disabled}>
                <Close />
              </IconButton>
            </Tooltip>
          ) : null,
      }}
    />
  );
};

const SearchComponent = ({
  query,
  setQuery,
  selected,
  setSelected,
  disabled,
  loading,
}: {
  query: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  selected: AccountSearchOption | undefined;
  setSelected: React.Dispatch<
    React.SetStateAction<AccountSearchOption | undefined>
  >;
  disabled?: boolean;
  loading?: boolean;
}) => {
  const [loadedData, setLoadedData] = useState<cardData | undefined>(undefined);
  const { warning } = useAlert();

  useEffect(() => {
    const fetchData = async () => {
      try {
        let newEmail: string | undefined = undefined;
        let newLogo: string | undefined = undefined;
        let newAddress: Address | undefined = undefined;

        if (selected?.type === "organization") {
          const org = selected.data as ESOrganization;
          newEmail = org.email;
          newLogo = org.logo;
          newAddress = await AddressAPI.getOrganizationAddressByLabel(
            org.id,
            OrganizationAddressLabels.PRIMARY,
          ).then((addr) => addr ?? undefined);
        } else if (selected?.type === "member") {
          const member = selected.data as ESMember;
          const org = await OrganizationAPI.get(member.organizationId);
          newEmail = await ContactAPI.findOneByDetailsGlobal(
            member.organizationId,
            ContactLabels.PRIMARY,
            ContactType.EMAIL,
            member.user_id,
          ).then((found) => found?.value ?? "");
          newLogo = org?.theme?.shortLogoUrl;
        } else if (selected?.type === "account") {
          const accountId = (selected.data as Account)?.id;
          const fullAccount = await AccountAPI.get(accountId);
          const orgId = fullAccount.external_org_id;
          if (orgId) {
            newAddress =
              (await AddressAPI.getOrganizationAddressByLabel(
                orgId,
                OrganizationAddressLabels.PRIMARY,
              )) || undefined;
          }
          newEmail = (selected.data as ESAccount)?.primary_email;

          if (orgId) {
            const org = await OrganizationAPI.get(orgId);
            newLogo = org?.theme?.shortLogoUrl;
          }
        }

        // Update state with fetched data
        // so the new selection displays all info simultaneously
        setLoadedData({
          name: selected?.name,
          secondaryName: selected?.name_secondary,
          hasEmail: !!newEmail && newEmail.length > 0,
          email: newEmail,
          logo: newLogo,
          address: newAddress,
        });
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoadedData(undefined);
        setSelected(undefined);
        warning(
          "An error occurred while loading the selected account details.",
        );
      }
    };

    if (selected) {
      fetchData();
    } else {
      setLoadedData(undefined);
    }
  }, [selected, setSelected, warning]);

  return selected && selected.name !== undefined && loadedData ? (
    <OptionSelectedCard
      setSelected={setSelected}
      disabled={disabled}
      loadedData={loadedData}
    />
  ) : (
    <SearchBar
      query={query}
      setQuery={setQuery}
      disabled={disabled}
      loading={loading}
    />
  );
};

const WrapperComponent = ({
  children,
  selected,
  props,
}: {
  children: React.ReactNode;
  selected?: AccountSearchOption;
  props: TaskExecutionComponentProps<
    TaskCreateAccount,
    TaskCreateAccountExecution
  >;
}) => {
  const { onExecute, disabled, actionBarVisible, task, taskExecution } =
    useAccountExecution(props);

  const isDisabled = disabled || taskExecution?.completed_at != null;

  return (
    <Box
      sx={{
        height: "600px",
        maxHeight: "100%",
        width: "100%",
        p: 2,
        bgcolor: "background.paper",
        borderRadius: 1,
        display: "flex",
        flexShrink: 1,
        flexDirection: "column",
        overflowY: "auto",
      }}
    >
      <Typography variant="h4" sx={{ mb: 2, ml: 1 }}>
        {task.name}
      </Typography>

      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
        }}
      >
        {children}
      </Box>
      {actionBarVisible && (
        <SubmitButton
          variant="contained"
          size="small"
          sx={{
            mt: 2,
            width: "auto",
            alignSelf: "flex-start",
            ml: 1,
          }}
          disabled={!selected || isDisabled}
          onSubmit={() => onExecute(selected)}
        >
          Continue
        </SubmitButton>
      )}
    </Box>
  );
};

const EmptyListComponent = ({
  query,
  setQuery,
  setSelected,
  emptyDirectory,
  props,
}: {
  query: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  setSelected: React.Dispatch<
    React.SetStateAction<AccountSearchOption | undefined>
  >;
  emptyDirectory: boolean;
  props: TaskExecutionComponentProps<
    TaskCreateAccount,
    TaskCreateAccountExecution
  >;
}) => {
  const {
    accountType,
    organization,
    allAccounts,
    findAccount,
    addAccounts,
    executeAfterCreated,
    automaticallyExecute,
  } = useAccountExecution(props);
  return (
    <AccountCreateSimpleForm
      accountType={accountType}
      organizationId={organization!.id}
      account={{
        account_name: query?.trim(),
        primary_email: query && query.indexOf("@") > 0 ? query?.trim() : "",
      }}
      onClose={() => {}}
      onCreate={(account) => {
        const existing = findAccount(account);
        if (!existing) {
          setQuery(getAccountName(account));
          addAccounts([account]);
        }
        setSelected({
          type: "account",
          data: account,
          name: getAccountName(account),
        });
        if (automaticallyExecute) executeAfterCreated(account);
      }}
      setSelected={setSelected}
      query={query}
      setQuery={setQuery}
      allAccounts={allAccounts}
      isEmpty={emptyDirectory}
    />
  );
};

const AccountSearchDirectory = (
  props: TaskExecutionComponentProps<
    TaskCreateAccount,
    TaskCreateAccountExecution
  >,
) => {
  const {
    selected,
    setSelected,
    disabled,
    fetchAccounts,
    allAccounts,
    results,
    loading,
  } = useAccountExecution(props);

  const RenderWrapperComponent = useCallback(
    (wrapperProps: {
      children: React.ReactNode;
      selected?: AccountSearchOption;
    }) => <WrapperComponent {...wrapperProps} props={props} />,
    [props],
  );

  const RenderEmptyListComponent = useCallback(
    (emptyProps: {
      query: string;
      setQuery: React.Dispatch<React.SetStateAction<string>>;
      setSelected: React.Dispatch<
        React.SetStateAction<AccountSearchOption | undefined>
      >;
      emptyDirectory: boolean;
    }) => (
      <EmptyListComponent
        {...emptyProps}
        emptyDirectory={allAccounts.length === 0}
        props={props}
      />
    ),
    [allAccounts.length, props],
  );

  return (
    <SearchDirectory<AccountSearchOption>
      options={results}
      setOptions={fetchAccounts}
      SearchComponent={SearchComponent}
      EmptyListComponent={RenderEmptyListComponent}
      WrapperComponent={RenderWrapperComponent}
      globalSelected={selected}
      setGlobalSelected={setSelected}
      disabled={disabled}
      loading={loading}
    />
  );
};

export default AccountSearchDirectory;
